import {SxProps, Theme} from '@mui/material';
import theme from 'src/theme';

/** The shared detail-page card. */
export const cardSx: SxProps<Theme> = {
    background: theme.palette.common.white,
    borderRadius: '14px',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 4px 12px rgba(0,0,0,0.04)',
    overflow: 'hidden',
};

/**
 * The map and the photograph, across the top.
 *
 * A location is a place before it is a record, and the map had a third of one row shared with a
 * photograph and eleven fields. The proportions no longer move with the reader's permission — that
 * row was `lg={canReadDetails ? 6 : 4}` beside `lg={canReadDetails ? 3.5 : 5.5}`, so two people
 * opening the same location saw differently shaped pages.
 */
export const bannerSx: SxProps<Theme> = {
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', md: '1.7fr 1fr'},
    gap: 1.5,
    // Inset by the card's own padding rather than bleeding to its edges: at full bleed and 200px the
    // first screen of a location was a picture before it was a location.
    px: 2,
    pt: 2,
};

/** Both framed, so they read as content inside the card rather than as the card's top edge. */
const tileSx: SxProps<Theme> = {
    height: '225px',
    borderRadius: '10px',
    border: `1px solid ${theme.palette.neutral[200]}`,
    overflow: 'hidden',
};

export const mapSx: SxProps<Theme> = {
    ...tileSx as object,
    '& > div': {height: '100%'},
};

export const photoSx: SxProps<Theme> = {
    ...tileSx as object,
    position: 'relative',
    background: theme.palette.neutral[50],
    '& img': {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
    },
};

export const headerSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 2.25,
    flexWrap: 'wrap',
    px: 3,
    // No rule above it any more: the banner is inset, so a border across the card would cut between
    // two things that already sit apart.
    pt: 2.5,
    pb: 3,
};

export const badgeSx: SxProps<Theme> = {
    width: '52px',
    height: '52px',
    borderRadius: '13px',
    background: `linear-gradient(135deg, ${theme.palette.primaryColor[50]} 0%, ${theme.palette.primaryColor[100]} 100%)`,
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& svg': {fontSize: '28px'},
};

export const titleSx: SxProps<Theme> = {
    fontSize: '1.5rem',
    fontWeight: 600,
    letterSpacing: '-0.015em',
};

export const subtitleSx: SxProps<Theme> = {
    fontSize: '0.75rem',
    color: theme.palette.neutral[500],
    mt: 0.4,
};

export const typePillSx: SxProps<Theme> = {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: '0.6875rem',
    fontWeight: 600,
    px: 1.25,
    py: 0.5,
    borderRadius: '999px',
    whiteSpace: 'nowrap',
    background: theme.palette.primaryColor.A50,
    color: theme.palette.primary.dark,
    border: `1px solid ${theme.palette.primaryColor.A100}`,
};

export const factsSx: SxProps<Theme> = {
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)'},
    borderTop: `1px solid ${theme.palette.neutral[100]}`,
};

export const factSx: SxProps<Theme> = {
    px: 2.75,
    py: 2.5,
    borderRight: `1px solid ${theme.palette.neutral[100]}`,
    '&:last-of-type': {borderRight: 'none'},
};

export const factLabelSx: SxProps<Theme> = {
    fontSize: '0.625rem',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: theme.palette.neutral[500],
};

export const factValueSx: SxProps<Theme> = {
    fontSize: '0.875rem',
    fontWeight: 500,
    mt: 0.4,
};

export const factNoteSx: SxProps<Theme> = {
    fontSize: '0.75rem',
    color: theme.palette.neutral[500],
    mt: 0.3,
};

export const factEmptySx: SxProps<Theme> = {
    fontSize: '0.875rem',
    fontWeight: 400,
    fontStyle: 'italic',
    color: theme.palette.neutral[400],
    mt: 0.4,
};

/** The money card: four fields that were scattered between the two sub-columns of the old form. */
export const sectionHeaderSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
    px: 3,
    py: 2,
};

export const sectionBadgeSx: SxProps<Theme> = {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    background: `linear-gradient(135deg, ${theme.palette.primaryColor[50]} 0%, ${theme.palette.primaryColor[100]} 100%)`,
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& svg': {fontSize: '18px'},
};

export const sectionTitleSx: SxProps<Theme> = {
    fontSize: '1rem',
    fontWeight: 600,
    color: theme.palette.primary.main,
};

/** Under the cost, where there is room for words: three unlabelled glyphs in a row said nothing. */
export const costActionsSx: SxProps<Theme> = {
    display: 'flex',
    gap: 1,
    mt: 1.25,
    flexWrap: 'wrap',
};
